import React, { useEffect, useMemo, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, RefreshCw, Search, ExternalLink, List, CalendarDays } from "lucide-react";
import api from "../../lib/api";
import { STATUS_COLOR } from "./common";

const SOURCE = {
  website: { label: "Website", color: "#9E4747" },
  direct: { label: "Direct", color: "#9E4747" },
  manual: { label: "Manual", color: "#1D2421" },
  airbnb: { label: "Airbnb", color: "#FF5A5F" },
  "booking.com": { label: "Booking.com", color: "#003580" },
  booking: { label: "Booking.com", color: "#003580" },
  expedia: { label: "Expedia", color: "#FEB700", text: "#1D2421" },
  vrbo: { label: "Vrbo", color: "#1A2B49" },
  "trip.com": { label: "Trip.com", color: "#287DFC" },
  agoda: { label: "Agoda", color: "#5C2D91" },
  "google vacation rentals": { label: "Google", color: "#4285F4" },
  ical: { label: "OTA (iCal)", color: "#78716C" },
};
const srcMeta = (s) => SOURCE[(s || "website").toLowerCase()] || { label: s || "Other", color: "#78716C" };

const MONTHS = ["January", "February", "March", "April", "May", "June", "July",
  "August", "September", "October", "November", "December"];
const WD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const pad = (n) => String(n).padStart(2, "0");
const ymd = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;

function Badge({ source, href }) {
  const m = srcMeta(source);
  const cls = "text-[11px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap inline-flex items-center gap-1";
  const style = { backgroundColor: m.color, color: m.text || "#fff" };
  if (href) return <a href={href} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className={`${cls} hover:brightness-110`} style={style} data-testid={`source-badge-${source}`} title={`Open reservation on ${m.label}`}>{m.label} <ExternalLink size={10} /></a>;
  return <span className={cls} style={style} data-testid={`source-badge-${source}`}>{m.label}</span>;
}

export default function UnifiedCalendar() {
  const [rows, setRows] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("list");
  const [q, setQ] = useState("");
  const [room, setRoom] = useState("all");
  const [source, setSource] = useState("all");
  const now = new Date();
  const [mv, setMv] = useState({ y: now.getFullYear(), m: now.getMonth() });

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.get("/admin/reservations"), api.get("/rooms")])
      .then(([r, rm]) => { setRows(r.data || []); setRooms(rm.data || []); })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const sources = useMemo(() => [...new Set(rows.map((r) => r.source))], [rows]);
  const filtered = useMemo(() => rows.filter((r) => {
    if (room !== "all" && r.room_id !== room) return false;
    if (source !== "all" && r.source !== source) return false;
    if (q) {
      const s = q.toLowerCase();
      if (!(`${r.guest_name} ${r.guest_email} ${r.room_name} ${r.code || ""}`.toLowerCase().includes(s))) return false;
    }
    return true;
  }), [rows, room, source, q]);

  const shift = (d) => setMv((v) => { const nm = v.m + d; return { y: v.y + Math.floor(nm / 12), m: ((nm % 12) + 12) % 12 }; });
  const cells = useMemo(() => {
    const lead = new Date(mv.y, mv.m, 1).getDay();
    const dim = new Date(mv.y, mv.m + 1, 0).getDate();
    return [...Array(lead).fill(null), ...Array.from({ length: dim }, (_, i) => i + 1)];
  }, [mv]);
  const dayRes = useCallback((D) => filtered.filter((r) => r.check_in <= D && D < r.check_out), [filtered]);
  const todayStr = ymd(now.getFullYear(), now.getMonth(), now.getDate());

  return (
    <div className="space-y-4" data-testid="unified-calendar">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search guest, email or code…"
            data-testid="uc-search" className="w-full rounded-lg border border-stone-200 pl-9 pr-3 py-2 text-sm" />
        </div>
        <select value={room} onChange={(e) => setRoom(e.target.value)} data-testid="uc-room-filter" className="rounded-lg border border-stone-200 px-3 py-2 text-sm">
          <option value="all">All properties</option>
          {rooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
        <select value={source} onChange={(e) => setSource(e.target.value)} data-testid="uc-source-filter" className="rounded-lg border border-stone-200 px-3 py-2 text-sm">
          <option value="all">All sources</option>
          {sources.map((s) => <option key={s} value={s}>{srcMeta(s).label}</option>)}
        </select>
        <div className="flex rounded-lg border border-stone-200 overflow-hidden">
          <button onClick={() => setView("list")} data-testid="uc-view-list" className={`px-3 py-2 text-sm flex items-center gap-1 ${view === "list" ? "bg-rose text-white" : "bg-white text-forest"}`}><List size={14} /> List</button>
          <button onClick={() => setView("month")} data-testid="uc-view-month" className={`px-3 py-2 text-sm flex items-center gap-1 ${view === "month" ? "bg-rose text-white" : "bg-white text-forest"}`}><CalendarDays size={14} /> Month</button>
        </div>
        <button onClick={load} data-testid="uc-refresh" className="p-2 rounded-lg hover:bg-sand text-rose" aria-label="Refresh"><RefreshCw size={16} className={loading ? "animate-spin" : ""} /></button>
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {sources.map((s) => <span key={s} className="flex items-center gap-1.5 text-xs text-stone-600"><span className="h-3 w-3 rounded" style={{ backgroundColor: srcMeta(s).color }} /> {srcMeta(s).label}</span>)}
      </div>

      {view === "list" ? (
        <div className="card-lux overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="uc-table">
              <thead className="bg-sand/60 text-stone-500 text-xs uppercase tracking-wide">
                <tr><th className="text-left p-3">Guest</th><th className="text-left p-3">Arrival</th><th className="text-left p-3">Departure</th><th className="text-left p-3">Property</th><th className="text-left p-3">Source</th><th className="text-left p-3">Paid / Total</th><th className="text-left p-3">Status</th><th className="text-left p-3">Link</th></tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} onClick={() => r.deep_link && window.open(r.deep_link, "_blank", "noopener")} className={`border-t border-stone-100 ${r.deep_link ? "cursor-pointer hover:bg-sand/40" : ""}`} data-testid={`uc-row-${r.id}`}>
                    <td className="p-3"><div className="font-medium text-forest">{r.guest_name || "—"}</div><div className="text-xs text-stone-400">{r.guest_email || (r.code ? r.code : "")}</div></td>
                    <td className="p-3 text-xs whitespace-nowrap">{r.check_in}</td>
                    <td className="p-3 text-xs whitespace-nowrap">{r.check_out}</td>
                    <td className="p-3">{r.room_name}</td>
                    <td className="p-3"><Badge source={r.source} href={r.deep_link} /></td>
                    <td className="p-3 whitespace-nowrap">{r.amount == null ? <span className="text-stone-400 text-xs">—</span> : <><span className="font-semibold">${r.amount_paid || 0}</span><span className="text-stone-400"> / ${r.amount}</span></>}</td>
                    <td className="p-3"><span className={`text-xs px-2 py-1 rounded-full font-medium ${STATUS_COLOR[r.status] || "bg-stone-100 text-stone-600"}`}>{(r.status || "").replace("_", " ")}</span></td>
                    <td className="p-3">{r.deep_link ? <a href={r.deep_link} target="_blank" rel="noopener noreferrer" data-testid={`uc-link-${r.id}`} className="text-rose hover:text-forest inline-flex" title={`Open on ${srcMeta(r.source).label}`}><ExternalLink size={15} /></a> : <span className="text-stone-300">—</span>}</td>
                  </tr>
                ))}
                {filtered.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-stone-400">No reservations match your filters.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="card-lux p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-serif text-xl text-forest">Reservations</h3>
            <div className="flex items-center gap-1">
              <button onClick={() => shift(-1)} data-testid="uc-prev" className="p-2 rounded-lg hover:bg-sand"><ChevronLeft size={16} /></button>
              <span className="text-sm font-semibold text-forest w-36 text-center" data-testid="uc-month">{MONTHS[mv.m]} {mv.y}</span>
              <button onClick={() => shift(1)} data-testid="uc-next" className="p-2 rounded-lg hover:bg-sand"><ChevronRight size={16} /></button>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-1">
            {WD.map((w) => <div key={w} className="text-[11px] font-semibold uppercase tracking-wide text-stone-400 py-1 text-center">{w}</div>)}
            {cells.map((d, i) => {
              if (d === null) return <div key={`e${i}`} />;
              const D = ymd(mv.y, mv.m, d);
              const res = dayRes(D);
              const isToday = D === todayStr;
              return (
                <div key={D} data-testid={`uc-day-${D}`} className={`min-h-[70px] rounded-lg p-1 bg-sand/40 ${isToday ? "ring-2 ring-forest" : ""}`}>
                  <div className="text-xs font-bold text-stone-500">{d}</div>
                  <div className="space-y-0.5 mt-0.5">
                    {res.slice(0, 3).map((r) => {
                      const chip = (
                        <div title={`${r.guest_name} · ${r.room_name} · ${srcMeta(r.source).label}${r.deep_link ? " · click to open" : ""}`}
                          className="text-[9px] leading-tight text-white rounded px-1 py-0.5 truncate" style={{ backgroundColor: srcMeta(r.source).color }}>
                          {r.guest_name || srcMeta(r.source).label}
                        </div>
                      );
                      return r.deep_link
                        ? <a key={r.id} href={r.deep_link} target="_blank" rel="noopener noreferrer" className="block hover:brightness-110">{chip}</a>
                        : <div key={r.id}>{chip}</div>;
                    })}
                    {res.length > 3 && <div className="text-[9px] text-stone-500 px-1">+{res.length - 3} more</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
